import { useMemo } from "react";

import {
  Capabilities,
  OrganizationRoles,
  Roles,
} from "@/shared/utils/constants";
import { hasCapability } from "@/shared/utils/roles";
import { OrganizationPermissions } from "@/features/organizations/hooks/types";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import { FullUserResponse } from "@/redux/users/types";

export const useOrganizationPermissions = (
  currentUser: FullUserResponse | null | undefined,
  organization: BaseOrganizationResponse | null | undefined,
): OrganizationPermissions => {
  return useMemo(() => {
    if (!currentUser) {
      return {
        canEdit: false,
        canDelete: false,
        canCreate: false,
        canImport: false,
        canViewOverviewTab: false,
        canViewInvitesTab: false,
        canViewMembersTab: false,
        canViewEvidenceTab: false,
        canViewReportsTab: false,
        canViewSettingsTab: false,
        canViewApplicationsTab: false,
        canManageDomains: false,
        canExportData: false,
      };
    }

    const isMemberOfOrganization =
      currentUser.organization?.id == organization?.id;
    const isAdmin =
      currentUser.organizationRole === OrganizationRoles.ADMIN.value;
    const isSupervisor =
      currentUser.organizationRole === OrganizationRoles.SUPERVISOR.value;
    const isGuest =
      currentUser.organizationRole === OrganizationRoles.GUEST.value;
    const canAccessEvidence = hasCapability(
      currentUser,
      Capabilities.ACCESS_EVIDENCE,
    );
    const canAccessSurvey = hasCapability(
      currentUser,
      Capabilities.ACCESS_SURVEY,
    );
    const canAccessMembers = hasCapability(
      currentUser,
      Capabilities.ACCESS_MEMBERS,
    );

    const isAppAdmin = currentUser.role === Roles.ADMIN.value;
    const isUnaffiliated = !!organization?.isUnaffiliated;
    const canManage =
      isAppAdmin || (isMemberOfOrganization && (isAdmin || isSupervisor));

    return {
      canEdit:
        (isMemberOfOrganization && (isAdmin || isSupervisor)) || isAppAdmin,
      canDelete: isAppAdmin || isAdmin,
      canCreate: isAppAdmin,
      canImport: isAppAdmin,
      canViewOverviewTab: true,
      canViewInvitesTab: !isUnaffiliated && canManage,
      canViewMembersTab: canAccessMembers,
      canViewEvidenceTab: !isUnaffiliated && !isGuest && canAccessEvidence,
      canViewReportsTab: !isUnaffiliated && canAccessSurvey,
      canViewSettingsTab: !isUnaffiliated && canManage,
      canViewApplicationsTab: !isUnaffiliated && canManage,
      canManageDomains: canManage,
      canExportData: isAppAdmin || (isMemberOfOrganization && isAdmin),
    };
  }, [currentUser, organization]);
};
